/*
 * <menu-tabs>
 *
 * Switches the menu card panels above the drawer menu.
 *
 * The markup is the theme's own tab contract (.tabs / .tab-link / .tab-content
 * / .current) so the strip inherits its styling and horizontal scrolling from
 * theme.css. The theme's GlobalTabs driver is only registered for the product,
 * related and product-grid sections, so rather than editing the compiled
 * theme.js this supplies its own controller over the same classes. It uses its
 * own element name and hooks, so nothing double-binds if the header is ever
 * registered with the theme's tabs module.
 */

(function () {
  const SELECTORS = {
    strip: '.tabs',
    tab: '[role="tab"]',
    panel: '[role="tabpanel"]',
  };

  const CURRENT = 'current';

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class MenuTabs extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
    }

    connectedCallback() {
      this.strip = this.querySelector(SELECTORS.strip);
      this.tabs = Array.from(this.querySelectorAll(SELECTORS.tab));
      this.panels = Array.from(this.querySelectorAll(SELECTORS.panel));

      // One tab is just a heading; leave the server-rendered state alone.
      if (this.tabs.length < 2 || this.tabs.length !== this.panels.length) return;

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    onClick(event) {
      const tab = event.target.closest(SELECTORS.tab);
      if (!tab || !this.contains(tab)) return;

      event.preventDefault();
      this.activate(this.tabs.indexOf(tab), false);
    }

    onKeydown(event) {
      if (!event.target.closest(SELECTORS.tab)) return;

      const last = this.tabs.length - 1;
      const from = this.tabs.indexOf(event.target.closest(SELECTORS.tab));
      let next = null;

      switch (event.key) {
        case 'ArrowRight':
          next = from === last ? 0 : from + 1;
          break;
        case 'ArrowLeft':
          next = from === 0 ? last : from - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = last;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activate(next);
    }

    /*
     * Selecting a block in the theme editor should reveal the tab it belongs
     * to, otherwise a merchant edits tab three while looking at tab one.
     */
    onBlockSelect(event) {
      const panel = event.target.closest?.(SELECTORS.panel);
      if (!panel || !this.contains(panel)) return;

      this.activate(this.panels.indexOf(panel), false);
    }

    activate(index, focus = true) {
      if (index < 0 || index >= this.tabs.length) return;

      this.tabs.forEach((tab, i) => {
        const isActive = i === index;

        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
        tab.closest('li')?.classList.toggle(CURRENT, isActive);
        this.panels[i].classList.toggle(CURRENT, isActive);
      });

      if (focus) this.tabs[index].focus();
      this.scrollTabIntoView(index);
    }

    /*
     * Scroll the strip itself rather than calling scrollIntoView, which would
     * also scroll the drawer and the page behind it.
     */
    scrollTabIntoView(index) {
      const item = this.tabs[index].closest('li');
      if (!this.strip || !item) return;

      const left = item.offsetLeft - (this.strip.clientWidth - item.offsetWidth) / 2;

      this.strip.scrollTo({
        left: Math.max(0, left),
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    }
  }

  if (!customElements.get('menu-tabs')) {
    customElements.define('menu-tabs', MenuTabs);
  }
})();
